import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reports } from '../api/methods';
export const reportKeys = {
    arLag: (range) => ['reports', 'ar-lag', range?.from ?? null, range?.to ?? null],
    techBillable: (range) => ['reports', 'tech-billable', range?.from ?? null, range?.to ?? null],
    laborCaptureWireless: (run) => [
        'reports',
        'labor-capture-wireless',
        run?.from ?? null,
        run?.to ?? null,
        run?.worker_type ?? null,
        run?.shop ?? null,
        // Ids join into one key part: two runs differing only in who was selected
        // must not share a cache entry.
        run?.employee_ids ? [...run.employee_ids].sort().join(',') : null,
    ],
    laborCaptureWorkPresets: () => ['reports', 'labor-capture-wireless', 'presets'],
    metadata: () => ['reports', 'metadata'],
    saved: () => ['reports', 'saved'],
    run: (config) => [
        'reports',
        'run',
        config?.metrics.join(',') ?? null,
        config?.group_by ?? null,
        config?.from ?? null,
        config?.to ?? null,
    ],
};
// 4xx here is deterministic (e.g. 403 if the user lost financial visibility) —
// don't retry it; only retry genuine 5xx/network once.
function retryReports(failureCount, error) {
    const status = error?.status;
    if (typeof status === 'number' && status >= 400 && status < 500)
        return false;
    return failureCount < 1;
}
export function useArLagReport(range) {
    return useQuery({
        queryKey: reportKeys.arLag(range),
        queryFn: () => reports.arLag(range),
        retry: retryReports,
    });
}
export function useTechBillableReport(range) {
    return useQuery({
        queryKey: reportKeys.techBillable(range),
        queryFn: () => reports.techBillable(range),
        retry: retryReports,
    });
}
/**
 * Run only the committed range AND filters. Passing null keeps the report idle
 * while the setup fields are being edited.
 */
export function useLaborCaptureWirelessReport(run) {
    return useQuery({
        queryKey: reportKeys.laborCaptureWireless(run),
        queryFn: async ({ signal }) => (await reports.laborCaptureWireless({
            range_start: run?.from ?? '',
            range_end: run?.to ?? '',
            worker_type: run?.worker_type ?? 'all',
            shop: run?.shop ?? 'all',
            // Absent means everyone; an empty array is a 400, never a silent widen.
            ...(run?.employee_ids?.length ? { employee_ids: run.employee_ids } : {}),
        }, signal)).report,
        enabled: Boolean(run?.from && run?.to),
        retry: false,
    });
}
export function useLaborCaptureWorkPresets() {
    return useQuery({
        queryKey: reportKeys.laborCaptureWorkPresets(),
        queryFn: async () => (await reports.listLaborCaptureWorkPresets()).presets,
        retry: retryReports,
    });
}
export function useCreateLaborCaptureWorkPreset() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload) => (await reports.createLaborCaptureWorkPreset(payload)).preset,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: reportKeys.laborCaptureWorkPresets() });
        },
    });
}
export function useDeleteLaborCaptureWorkPreset() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => reports.deleteLaborCaptureWorkPreset(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: reportKeys.laborCaptureWorkPresets() });
        },
    });
}
// ── Report builder (#873) ───────────────────────────────────────────────────
/**
 * Metric + group-by registry. Long-lived: it only changes when the backend
 * registry does, so the builder can hold it for the session.
 */
export function useReportMetadata() {
    return useQuery({
        queryKey: reportKeys.metadata(),
        queryFn: () => reports.metadata(),
        staleTime: 30 * 60 * 1000,
        retry: retryReports,
    });
}
/**
 * Run a builder config. Pass `null` to stay idle — the page only queries after
 * the user presses Run, so typing in the builder never fires a request.
 */
export function useRunReport(config) {
    return useQuery({
        queryKey: reportKeys.run(config),
        queryFn: () => reports.run(config),
        enabled: Boolean(config && config.metrics.length > 0),
        retry: retryReports,
    });
}
export function useSavedReports() {
    return useQuery({
        queryKey: reportKeys.saved(),
        queryFn: async () => (await reports.listSaved()).reports,
        retry: retryReports,
    });
}
export function useCreateSavedReport() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload) => (await reports.createSaved(payload)).report,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: reportKeys.saved() });
        },
    });
}
export function useUpdateSavedReport() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, patch }) => (await reports.updateSaved(id, patch)).report,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: reportKeys.saved() });
        },
    });
}
export function useDeleteSavedReport() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => reports.deleteSaved(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: reportKeys.saved() });
        },
    });
}
//# sourceMappingURL=useReports.js.map